import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/admin/api";
import { Eyebrow } from "@/components/comiq/Eyebrow";
import { COLORS } from "@/comiq/tokens";
import { toast } from "sonner";
import { Plus, Check, Archive, CopyPlus, Pencil, X, FileCheck } from "lucide-react";

const FIELDS = [
  { key: "title", label: "Judul consent" },
  { key: "purpose", label: "Tujuan penelitian", area: true },
  { key: "data_collected", label: "Data yang dikumpulkan", area: true },
  { key: "confidentiality", label: "Kerahasiaan data", area: true },
  { key: "voluntary", label: "Sifat partisipasi sukarela", area: true },
  { key: "right_to_withdraw", label: "Hak untuk berhenti", area: true },
  { key: "contact", label: "Kontak" },
  { key: "checkbox_label", label: "Label checkbox persetujuan" },
];

const EMPTY = {
  title: "", purpose: "", data_collected: "", confidentiality: "", voluntary: "",
  right_to_withdraw: "", contact: "survey@comiq.id",
  checkbox_label: "Saya telah membaca dan menyetujui persetujuan partisipasi ini.",
};

const STATUS_STYLE = {
  draft: { color: "#B45309", background: "#FEF3C7", label: "Draft" },
  active: { color: "#15803D", background: "#DCFCE7", label: "Aktif" },
  archived: { color: "#57534E", background: "#EEEAE0", label: "Archived" },
  internal_test: { color: "#57534E", background: "#EEEAE0", label: "Internal test" },
};

export default function Consents() {
  const [studies, setStudies] = useState([]);
  const [selected, setSelected] = useState(null);
  const [consents, setConsents] = useState([]);
  const [form, setForm] = useState(null); // null | { mode: "new" } | { mode: "edit", id }
  const [vals, setVals] = useState(EMPTY);
  const [searchParams] = useSearchParams();

  const loadConsents = (studyId) =>
    api.get(`/admin/studies/${studyId}/consents`).then((r) => setConsents(r.data)).catch(() => setConsents([]));

  useEffect(() => {
    api.get("/admin/studies").then((r) => setStudies(r.data)).catch(() => {});
    const s = searchParams.get("study");
    if (s) { setSelected(s); loadConsents(s); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pick = (studyId) => { setSelected(studyId); setForm(null); loadConsents(studyId); };

  const openNew = () => { setVals(EMPTY); setForm({ mode: "new" }); };
  const openEdit = (c) => {
    setVals(Object.fromEntries(FIELDS.map((f) => [f.key, c[f.key] || ""])));
    setForm({ mode: "edit", id: c.consent_id });
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (form.mode === "new") {
        await api.post(`/admin/studies/${selected}/consents`, vals);
        toast.success("Draft consent dibuat");
      } else {
        await api.put(`/admin/consents/${form.id}`, vals);
        toast.success("Draft consent diperbarui");
      }
      setForm(null);
      loadConsents(selected);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal menyimpan");
    }
  };

  const act = async (path, okMsg) => {
    try {
      await api.post(path);
      toast.success(okMsg);
      loadConsents(selected);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Aksi ditolak");
    }
  };

  const inputCls = "w-full rounded-comiq-sm border bg-white px-3 py-2 text-sm outline-none";
  const inputStyle = { borderColor: COLORS.line, color: COLORS.ink };
  const activeCount = consents.filter((c) => c.active).length;

  return (
    <div className="p-6">
      <Eyebrow>Etika &amp; Persetujuan</Eyebrow>
      <h1 className="font-serif-editorial mt-1 text-3xl" style={{ color: COLORS.ink }}>Consent Management</h1>
      <p className="mt-1 text-sm" style={{ color: COLORS.textSoft }}>
        Consent berlaku per <strong>studi</strong>. Hanya satu versi boleh aktif; versi aktif/arsip bersifat immutable —
        perubahan dibuat lewat versi baru.
      </p>

      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        <div className="rounded-comiq border bg-comiq-surface-alt p-5" style={{ borderColor: COLORS.line }}>
          <h2 className="text-sm font-semibold" style={{ color: COLORS.ink }}>Studi</h2>
          <div className="mt-3 space-y-1.5">
            {studies.map((s) => (
              <button
                key={s.study_id}
                onClick={() => pick(s.study_id)}
                data-testid={`consent-study-${s.study_id}`}
                className="block w-full rounded-comiq-sm border px-3 py-2.5 text-left text-sm transition-colors"
                style={{
                  borderColor: selected === s.study_id ? COLORS.orange : COLORS.line,
                  background: selected === s.study_id ? "rgba(242,84,27,0.06)" : "#fff",
                  color: COLORS.ink,
                }}
              >
                {s.title}
                {s.is_demo && <span className="ml-2 text-[10px] font-bold uppercase" style={{ color: "#B45309" }}>demo</span>}
              </button>
            ))}
          </div>
        </div>

        <div className="rounded-comiq border bg-comiq-surface-alt p-5 lg:col-span-2" style={{ borderColor: COLORS.line }}>
          {!selected && <p className="text-sm" style={{ color: COLORS.textSoft }}>Pilih studi untuk mengelola consent.</p>}
          {selected && (
            <>
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold" style={{ color: COLORS.ink }}>
                  Versi consent <span className="font-normal" style={{ color: COLORS.textSoft }}>({activeCount} aktif)</span>
                </h2>
                <button onClick={openNew} className="btn-comiq btn-accent !py-1.5 !text-xs" data-testid="consent-create-btn">
                  <Plus size={14} /> Buat consent baru
                </button>
              </div>

              <div className="mt-4 space-y-3" data-testid="consent-list">
                {consents.length === 0 && (
                  <p className="rounded-comiq-sm border px-3 py-3 text-sm" style={{ borderColor: COLORS.line, color: COLORS.textSoft }} data-testid="consent-empty">
                    Belum ada consent untuk studi ini — buat draft pertama.
                  </p>
                )}
                {consents.map((c) => {
                  const st = STATUS_STYLE[c.status] || STATUS_STYLE.draft;
                  return (
                    <div key={c.consent_id} className="rounded-comiq border bg-white px-4 py-3" style={{ borderColor: c.active ? "#86EFAC" : COLORS.line }} data-testid={`consent-row-${c.consent_id}`}>
                      <div className="flex flex-wrap items-center gap-3">
                        <span className="font-mono text-xs font-bold" style={{ color: COLORS.ink }}>v{c.version_no}</span>
                        <span className="text-sm font-semibold" style={{ color: COLORS.ink }}>{c.title}</span>
                        <span className="rounded-full px-2.5 py-1 text-xs font-semibold" style={{ color: st.color, background: st.background }} data-testid={`consent-status-${c.consent_id}`}>
                          {st.label}
                        </span>
                        <div className="ml-auto flex flex-wrap gap-2">
                          {c.status === "draft" && (
                            <>
                              <button onClick={() => openEdit(c)} className="btn-comiq btn-line !py-1 !text-xs" data-testid={`consent-edit-${c.consent_id}`}>
                                <Pencil size={12} /> Edit draft
                              </button>
                              <button onClick={() => act(`/admin/consents/${c.consent_id}/activate`, `Consent v${c.version_no} diaktifkan — versi aktif sebelumnya diarsipkan`)} className="btn-comiq btn-accent !py-1 !text-xs" data-testid={`consent-activate-${c.consent_id}`}>
                                <Check size={12} /> Aktifkan
                              </button>
                            </>
                          )}
                          {c.status === "active" && (
                            <button onClick={() => act(`/admin/consents/${c.consent_id}/archive`, "Consent diarsipkan")} className="btn-comiq btn-line !py-1 !text-xs" data-testid={`consent-archive-${c.consent_id}`}>
                              <Archive size={12} /> Arsipkan
                            </button>
                          )}
                          <button onClick={() => act(`/admin/consents/${c.consent_id}/new-version`, "Versi draft baru dibuat")} className="btn-comiq btn-line !py-1 !text-xs" data-testid={`consent-newver-${c.consent_id}`}>
                            <CopyPlus size={12} /> Buat versi berikutnya
                          </button>
                        </div>
                      </div>
                      <p className="mt-2 line-clamp-2 whitespace-pre-line text-xs" style={{ color: COLORS.textSoft }}>{c.body}</p>
                      <p className="mt-1 text-[11px]" style={{ color: COLORS.textSoft }}>
                        Checkbox: “{c.checkbox_label}” · Kontak: {c.contact}
                        {c.published_at ? ` · Aktif sejak ${new Date(c.published_at).toLocaleDateString("id-ID")}` : ""}
                      </p>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>

      {form && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="consent-form-modal">
          <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-comiq-lg border bg-comiq-surface-alt p-6" style={{ borderColor: COLORS.line }}>
            <div className="flex items-center justify-between">
              <h3 className="flex items-center gap-2 text-sm font-semibold" style={{ color: COLORS.ink }}>
                <FileCheck size={16} /> {form.mode === "new" ? "Draft consent baru" : "Edit draft consent"}
              </h3>
              <button onClick={() => setForm(null)} data-testid="consent-cancel-btn"><X size={16} style={{ color: COLORS.textSoft }} /></button>
            </div>
            <form onSubmit={save} className="mt-4 space-y-3" data-testid="consent-form">
              {FIELDS.map((f) => (
                <label key={f.key} className="block">
                  <span className="text-xs font-semibold" style={{ color: COLORS.ink }}>{f.label}</span>
                  {f.area ? (
                    <textarea
                      className={`${inputCls} mt-1 min-h-[72px]`}
                      style={inputStyle}
                      value={vals[f.key]}
                      onChange={(e) => setVals({ ...vals, [f.key]: e.target.value })}
                      required
                      data-testid={`consent-field-${f.key}`}
                    />
                  ) : (
                    <input
                      className={`${inputCls} mt-1`}
                      style={inputStyle}
                      value={vals[f.key]}
                      onChange={(e) => setVals({ ...vals, [f.key]: e.target.value })}
                      required
                      data-testid={`consent-field-${f.key}`}
                    />
                  )}
                </label>
              ))}
              <button className="btn-comiq btn-accent w-full justify-center" data-testid="consent-save-btn">
                {form.mode === "new" ? "Simpan draft" : "Simpan perubahan draft"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
